import { spawnSync } from "node:child_process";

import { ORGX_WIZARD_AUTH_PATH } from "../constants.js";
import {
  readWizardAuth,
  writeWizardAuth,
  type WizardAuthRecord,
} from "./auth-store.js";
import { refreshAccessToken } from "./oauth-client.js";
import {
  DEEPSEEK_PROFILE,
  inspectDeepseekRuntime,
  type DeepseekCommandRunner,
} from "../surfaces/deepseek.js";

export interface DeepseekLaunchResult {
  status: number;
}

interface DeepseekLauncherDependencies {
  authPath?: string;
  env?: NodeJS.ProcessEnv;
  readAuth?: typeof readWizardAuth;
  refreshToken?: typeof refreshAccessToken;
  runtimeRunner?: DeepseekCommandRunner;
  spawn?: typeof spawnSync;
  writeAuth?: typeof writeWizardAuth;
}

async function resolveFreshMcpAuth(
  deps: DeepseekLauncherDependencies,
): Promise<WizardAuthRecord> {
  const authPath = deps.authPath ?? ORGX_WIZARD_AUTH_PATH;
  const readAuth = deps.readAuth ?? readWizardAuth;
  const stored = await readAuth(authPath);
  if (!stored) {
    throw new Error("OrgX is not paired. Run orgx-wizard auth login first.");
  }

  if (stored.source !== "pkce") return stored;
  if (!stored.refreshToken || !stored.oauthClientId) {
    throw new Error("OrgX browser authorization cannot be refreshed. Run orgx-wizard auth login again.");
  }

  const refresh = deps.refreshToken ?? refreshAccessToken;
  let refreshed;
  try {
    refreshed = await refresh({
      refreshToken: stored.refreshToken,
      clientId: stored.oauthClientId,
    });
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    throw new Error(`OrgX authorization refresh failed. Run orgx-wizard auth login again. ${detail}`);
  }

  const writeAuth = deps.writeAuth ?? writeWizardAuth;
  return writeAuth(
    {
      apiKey: refreshed.access_token,
      baseUrl: stored.baseUrl,
      verifiedAt: new Date().toISOString(),
      source: "pkce",
      refreshToken: refreshed.refresh_token ?? stored.refreshToken,
      oauthClientId: stored.oauthClientId,
    },
    authPath,
  );
}

export async function launchDeepseekWithOrgx(
  prompt: readonly string[],
  deps: DeepseekLauncherDependencies = {},
): Promise<DeepseekLaunchResult> {
  if (prompt.length === 0 || prompt.every((part) => !part.trim())) {
    throw new Error("Give DeepSeek a prompt, for example: orgx-wizard deepseek \"What changed while I was away?\"");
  }

  const runtime = inspectDeepseekRuntime(deps.runtimeRunner);
  if (!runtime.installed) {
    throw new Error("DeepSeek Harness was not found. Run orgx-wizard setup after installing DSH.");
  }
  if (!runtime.supported) {
    throw new Error(`DeepSeek Harness ${runtime.version ?? "unknown"} is not supported by this OrgX plugin.`);
  }

  const auth = await resolveFreshMcpAuth(deps);
  const childEnv: NodeJS.ProcessEnv = {
    ...(deps.env ?? process.env),
    ORGX_MCP_ACCESS_TOKEN: auth.apiKey,
  };
  delete childEnv.ORGX_API_KEY;

  const spawn = deps.spawn ?? spawnSync;
  const result = spawn(
    "dsh",
    ["--profile", DEEPSEEK_PROFILE, prompt.join(" ")],
    {
      env: childEnv,
      stdio: "inherit",
    },
  );

  if (result.error) throw result.error;
  return { status: result.status ?? 1 };
}
